/**
 * EditorPage -- 3컬럼 Grid 청첩장 편집 페이지
 *
 * 데스크톱: EditorForm(5fr) | SectionNav(auto) | Preview(3fr)
 * 모바일: 전체 프리뷰 + 하단 MobileSectionNav + 바텀시트 편집
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useApiFetch } from '../../hooks/useApiFetch.js'
import { useInvitationStore } from '../../stores/invitationStore.js'
import { useAutoSave } from '../../components/editor/useAutoSave.js'
import { sanitizeSectionOrder, type SectionId } from '@cugumi/shared/schemas'
import { EditorHeader } from '../../components/editor/EditorHeader.js'
import { SectionNav } from '../../components/editor/SectionNav.js'
import { EditorForm } from '../../components/editor/EditorForm.js'
import { EditorPreview } from '../../components/editor/EditorPreview.js'
import { MobileSectionNav } from '../../components/editor/MobileSectionNav.js'
import { MobileBottomSheet } from '../../components/editor/MobileBottomSheet.js'
import { PreviewOverlay } from '../../components/editor/PreviewOverlay.js'
import { Spinner } from '../../components/ui/index.js'
import type { AllSectionId } from '../../components/editor/types.js'
import type { Invitation } from '@cugumi/shared/schemas'
import styles from './EditorPage.module.css'

export function EditorPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { data, error, isLoading } = useApiFetch<Invitation>(
    id ? `/api/invitations/${id}` : null,
  )

  const invitation = useInvitationStore((s) => s.invitation)
  const templateConfig = useInvitationStore((s) => s.templateConfig)
  const setInvitation = useInvitationStore((s) => s.setInvitation)
  const isDirty = useInvitationStore((s) => s.isDirty)
  const clear = useInvitationStore((s) => s.clear)
  const { saveStatus, save, publish } = useAutoSave(id!)

  const sectionOrder = useMemo(
    () => sanitizeSectionOrder(invitation?.settings?.sectionOrder as SectionId[] | undefined),
    [invitation?.settings?.sectionOrder],
  )

  // 신랑/신부 정보를 기본 선택
  const [activeSection, setActiveSection] = useState<AllSectionId>('parents')

  useEffect(() => {
    if (data) setInvitation(data)
  }, [data, setInvitation])

  useEffect(() => {
    return () => { clear() }
  }, [clear])

  // 미저장 변경사항 경고
  const isDirtyRef = useRef(isDirty)
  useEffect(() => { isDirtyRef.current = isDirty }, [isDirty])

  useEffect(() => {
    function onBeforeUnload(e: BeforeUnloadEvent) {
      if (isDirtyRef.current) e.preventDefault()
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [])

  // 항상 선택 (토글 없음)
  const handleSelectSection = useCallback((id: AllSectionId) => {
    setActiveSection(id)
  }, [])

  // 전체화면 미리보기
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)

  // 모바일 바텀시트 닫기
  const [mobileSheetSection, setMobileSheetSection] = useState<AllSectionId | null>(null)

  const handleMobileSelect = useCallback((id: AllSectionId) => {
    setMobileSheetSection(id)
  }, [])

  const handleMobileClose = useCallback(() => {
    setMobileSheetSection(null)
  }, [])

  const handlePreviewSectionClick = useCallback((sectionId: SectionId) => {
    setActiveSection(sectionId)
  }, [])

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Spinner size="lg" />
      </div>
    )
  }

  if (error || !invitation) {
    return (
      <div className={styles.error}>
        <h2>청첩장을 불러올 수 없습니다</h2>
        <p>{error ?? '데이터가 없습니다'}</p>
        <button onClick={() => navigate('/studio/invitations')} className={styles.backButton}>
          목록으로 돌아가기
        </button>
      </div>
    )
  }

  return (
    <div className={styles.root}>
      <EditorHeader
        invitation={invitation}
        saveStatus={saveStatus}
        onSave={save}
        onPublish={publish}
        onPreview={() => setIsPreviewOpen(true)}
        isDirty={isDirty}
      />

      <div className={styles.body}>
        {/* 데스크톱: 좌측 편집 폼 */}
        <div className={styles.editorForm}>
          <EditorForm
            invitation={invitation}
            activeSection={activeSection}
          />
        </div>

        {/* 데스크톱: 중앙 섹션 네비 */}
        <div className={styles.sectionNav}>
          <SectionNav
            activeSection={activeSection}
            onSelect={handleSelectSection}
            sectionOrder={sectionOrder}
            enabledSections={invitation.enabledSections}
          />
        </div>

        {/* 데스크톱: 우측 프리뷰 */}
        <div className={styles.previewArea}>
          <EditorPreview
            invitation={invitation}
            activeSection={activeSection as SectionId | null}
            onSectionClick={handlePreviewSectionClick}
            templateConfig={templateConfig}
          />
        </div>
      </div>

      {/* 모바일 하단 네비게이터 */}
      <MobileSectionNav
        activeSection={mobileSheetSection}
        onSelect={handleMobileSelect}
        sectionOrder={sectionOrder}
      />

      {/* 모바일 바텀시트 */}
      <MobileBottomSheet
        invitation={invitation}
        activeSection={mobileSheetSection}
        onClose={handleMobileClose}
      />

      {/* 전체화면 미리보기 오버레이 */}
      {isPreviewOpen && (
        <PreviewOverlay
          invitation={invitation}
          templateConfig={templateConfig}
          onClose={() => setIsPreviewOpen(false)}
        />
      )}
    </div>
  )
}
